/**
 * +field: the world (docs/design/zones.md Phase 1). Owns the "field session": one hero on a map of
 * the zone (town, field or the levelled starter dungeon), and everything around it:
 *
 *  - map loading: compile the hero (world/compile.ts), start a session with the hero alone
 *    (controller 'human', keystone level 0, no affixes, `arrive` from the saved position or the
 *    portal's mouth); portals save the location (`fieldMove`) and load the destination behind a
 *    short fade; the barrow entrance (kind 'dungeon') opens the Group Finder prompt instead;
 *  - XP: enemy deaths batch into `fieldKills` (world/killBatch.ts); the HUD's XP bar moves on every
 *    kill from a local prediction; a result with levels sends the sim `setLevel` and plays the
 *    LEVEL UP celebration (hud/field/celebrate.ts);
 *  - Auto (world/autoMode.ts): a grind bot brain drives the hero; manual input hands back
 *    control; portals are ignored while it runs;
 *  - town NPCs: the "[T] Talk" prompt in reach, click via renderer.npcAt; Elder Rowan's dialog,
 *    the Bounty Board, and the Smith / Quartermaster / Dungeon Board meta screens as overlays;
 *  - the levelled dungeon: Group Finder party at the hero's level, `dungeonEnter`, a normal run,
 *    `dungeonClear` on completion, back to the entrance with the reward banner;
 *  - +zone: waystones (NPCs with role 'waystone'): walking up attunes one (`waystoneAttune`); its
 *    panel lists the attuned others; Travel channels WAYSTONE_CHANNEL_MS (moving, taking damage or
 *    combat cancel it), then sends the sim interact 'waystone:<id>' (the sim checks place, combat
 *    and its cooldown);
 *  - the position is saved every SAVE_MS, on portals and on pagehide.
 *
 * HUD widgets read world/fieldView.ts; this class writes it.
 */
import type { BotBrain, ContentRegistry, DeepReadonly, DungeonDef, FieldAreaDef, FieldNpcDef, PortalDef, SimEvent, UnitId, World } from '@keystone/sim';
import { fieldAreaAt, fieldRegionAt } from '@keystone/sim';
import { type BotOptions, type GrindBrain, createBot, defaultCompanions } from '@keystone/bots';
import { type FieldGain, type Item, type Profile, type QuestCompletion, gridKeys, LEVEL_CAP, RESPEC_GOLD, addXp, boardUnlocked, bountyDef, currentChainStep, dungeonBoardOpen, heroSpecId, killGold, killXp, xpToNext, zoneQuests } from '@keystone/progression';
import { SPEC_LEVEL, specInfo } from '@keystone/content';
import { baseClassIdOf } from '@keystone/sim';
import { evolvedLines, specCards } from '../hud/field/specTrial'; // +specs
import { rewardPops } from '../hud/field/rewardPops';
import type { RewardRarity } from './rewardPopModel';
import type { Session, SessionEnd } from '../game/session';
import { startSession } from '../game/session';
import type { StartConfig } from '../hud/screens';
import type { GraphicsSettings, RendererKind } from '../render/settings';
import type { Renderer } from '../render/types';
import type { ProfileStore } from '../meta/profileStore';
import type { MetaRouter } from '../meta/context';
import { ROUTES } from '../meta/router';
import { metaBridge } from '../meta/metaBridge';
import { inRunView } from '../meta/inrunHud';
import { playSound } from '../audio/engine';
import { kmToast } from '../meta/kmmo/components';
import { GLIDE_TIP, jumpedFromHigh, onceFlag, puzzleTopsReached } from './skyTips'; // +glide
import { celebrations } from '../hud/field/celebrate';
import { closeTownPanel, openTownPanel, townPanelOpen } from '../hud/field/townPanel';
import { el } from '../hud/dom';
import { AutoMode } from './autoMode';
import { type MapKind, fieldView, resetFieldView } from './fieldView';
import { KillBatch, levelledKills } from './killBatch';
import { levelUpSummary } from './levelUpModel';
import { npcMarkers, trackerRows, waypointOn } from './trackerModel';
import { idleIntroGraphic } from '../hud/field/questPanels';
import { dungeonParty, dungeonRegistry, fieldRegistry, heroLevelOf } from './compile';
import { GatherController } from './gathering'; // +prof
import { SideQuestController } from './sideQuests'; // +quests
import { claimSecretKeys, heroWits, keySecretsOpened } from './gridKeys'; // +grid
import { forwardSimEvents, presenceEvent, simLocalState, startFieldPresence, stopFieldPresence } from '../net/fieldPresence'; // +presence

/** Save the hero's position this often (plus on portals and pagehide). */
export const SAVE_MS = 15_000;
/** +zone: a waystone's travel channel. */
export const WAYSTONE_CHANNEL_MS = 1500;
/** +zone: walk within this many yards of a waystone to attune it (progression checks 12). */
const WAYSTONE_ATTUNE_NEAR = 10;
/** +zone: Auto hunts within this many yards of where it was turned on (or in that region, within twice that). */
export const AUTO_HOME_YD = 55;
/** World upkeep (prompt, Auto status, kill flush) interval. */
const UPKEEP_MS = 200;
/** +specs: how long the Advancement Trial's "try it" lasts. */
export const TRIAL_MS = 30_000;
/** Talk prompt range from a town NPC (yards), and the farthest a click may talk from. */
const TALK_RANGE = 4.5;
const CLICK_RANGE = 12;
const FADE_MS = 280;

export interface WorldHost {
  canvasHost: HTMLElement;
  hudHost: HTMLElement;
  /** Base (uncompiled) content. */
  content: ContentRegistry;
  store: ProfileStore;
  router: MetaRouter;
  settings(): GraphicsSettings;
  rendererFactory(kind: RendererKind, registry: ContentRegistry): Promise<(host: HTMLElement) => Renderer>;
  /** Tear down whatever is on screen (meta screens, an old run); returns "still current?" for this load. */
  begin(): () => boolean;
  /** A world session is live (main keeps it for settings / the debug handle). */
  started(s: Session): void;
}

type Arrive = { pos: { x: number; y: number }; facing: number } | null;

interface Live {
  session: Session;
  mapId: string;
  map: DungeonDef;
  kind: MapKind;
  registry: ContentRegistry;
  heroId: UnitId;
  brain: BotBrain | null;
  batch: KillBatch;
  /** Ground pickups (XP wisps, gold, gear) waiting for `fieldPickups`, and their predicted XP. */
  pickups: { kind: 'gold' | 'xp' | 'item'; amount: number; rarity?: string }[];
  pickupXp: number;
  pickupSince: number | null;
  timer: ReturnType<typeof setInterval>;
  lastSave: number;
  /** Dungeon run: the field + portal to come back to. */
  back?: { mapId: string; portalId: string };
  /** +zone: the area the hero stands in (null = none / not tracked yet). */
  area: string | null;
  /** +zone: a waystone travel being channelled. */
  channel?: { to: string; name: string; at: number; pos: { x: number; y: number }; hp: number; el: HTMLElement } | null;
  /** +zone: waystones with an attune request in flight. */
  attuning?: Set<string>;
}

const dist = (a: { x: number; y: number }, b: { x: number; y: number }): number => Math.hypot(a.x - b.x, a.y - b.y);

export class WorldApp {
  private live: Live | null = null;
  private auto = new AutoMode('town');
  private loads = 0;
  private fade: HTMLElement | null = null;
  /** Banners to show once the next map is up (quest steps finished by a portal, a dungeon clear). */
  private after: (() => void)[] = [];
  private overlaySnapshot: string | null = null;
  private wasOverlay = false;
  private readonly onCanvasDown = (e: PointerEvent): void => this.canvasClick(e);
  private downAt: { x: number; y: number } | null = null;
  /** +specs: the Advancement Trial's 30 s preview (a local spec override; nothing is saved). */
  private trial: { specId: string; until: number; timer: ReturnType<typeof setInterval>; banner: HTMLElement; npcId: string } | null = null;

  /** +prof: herb / ore gathering (world/gathering.ts). */
  private readonly gather: GatherController;
  /** +quests: side quests (world/sideQuests.ts). */
  private readonly quests: SideQuestController;

  constructor(private readonly host: WorldHost) {
    this.gather = new GatherController({ store: host.store, canvasHost: host.canvasHost, autoOn: () => this.auto.active, stopAuto: () => this.toggleAuto(false), applyGain: (g) => this.applyGain(g) }); // +prof
    // +quests
    this.quests = new SideQuestController({
      store: host.store,
      hudHost: host.hudHost,
      names: () => this.names(),
      applyGain: (g) => this.applyGain(g),
      refresh: () => this.refresh(),
      savePosition: () => this.savePosition(),
      heroLevel: () => heroLevelOf(this.store.profile),
      mainQuest: () => {
        const c = this.store.profile.character;
        const t = c ? trackerRows(c, this.names(), heroLevelOf(this.store.profile)).find((r) => r.main) : undefined;
        return t ? { title: t.title, text: `${t.text}${t.max > 1 ? ` (${t.cur}/${t.max})` : ''}` } : null;
      },
    });
    host.router.subscribe(() => this.overlayChanged());
    host.canvasHost.addEventListener('pointerdown', (e) => (this.downAt = { x: e.clientX, y: e.clientY }), true);
    host.canvasHost.addEventListener('pointerup', this.onCanvasDown, true);
  }

  get session(): Session | null {
    return this.live?.session ?? null;
  }

  get mapId(): string | null {
    return this.live?.mapId ?? null;
  }

  private get store(): ProfileStore {
    return this.host.store;
  }

  /** Enter the world: character creation when there is none, else the saved map and position. */
  enter(): void {
    const p = this.store.profile;
    const c = p.character;
    if (!c) {
      this.dispose();
      this.host.router.reset(ROUTES.create, { preselect: p.mainClassId });
      return;
    }
    const map = this.host.content.dungeon(c.mapId);
    if (!map?.field) {
      // Saved inside a dungeon (or an unknown map): the zone's town.
      void this.load(zoneQuests(c.zoneId)?.townMapId ?? 'mossvale', null);
      return;
    }
    void this.load(c.mapId, c.pos ? { pos: c.pos, facing: c.facing } : null);
  }

  /** Save where the hero stands (fields and town only). */
  savePosition(): void {
    const l = this.live;
    if (!l || l.kind === 'dungeon') return;
    const u = l.session.sim.getWorld().units[l.heroId];
    if (!u) return;
    l.lastSave = performance.now();
    void this.store.act('fieldMove', { mapId: l.mapId, pos: { x: round2(u.pos.x), y: round2(u.pos.y) }, facing: round2(u.facing) });
  }

  /** Leave the world for an M+ key (saves the position first). */
  suspend(): void {
    this.savePosition();
    this.dispose();
  }

  dispose(): void {
    closeTownPanel();
    stopFieldPresence(); // +presence
    const l = this.live;
    this.live = null;
    if (l) {
      clearInterval(l.timer);
      this.flushKills(l);
      this.flushPickups(l, true);
      l.session.dispose();
    }
    metaBridge.setOverlayNote(null);
    if (inRunView.active) {
      inRunView.active = false;
      inRunView.version++;
    }
    resetFieldView();
    this.gather.detach(); // +prof
    this.quests.detach(); // +quests
  }

  // -------------------------------------------------------------------------
  // Map loading
  // -------------------------------------------------------------------------

  /** Load `mapId` (a field, the town or a levelled dungeon) with the hero at `arrive`. */
  async load(mapId: string, arrive: Arrive, opts: { back?: Live['back']; trialSpecId?: string } = {}): Promise<void> {
    const map = this.host.content.dungeon(mapId);
    const p = this.store.profile;
    const c = p.character;
    if (!map || !c) return this.enter();
    if (!opts.trialSpecId) this.clearTrial(); // +specs: any other load ends a spec preview
    const current = this.host.begin();
    await this.fadeOut();
    if (!current()) return;
    this.dispose();
    const kind: MapKind = map.field ? (map.field.kind === 'town' ? 'town' : 'field') : 'dungeon';
    const level = heroLevelOf(p);
    // +specs: the Group Finder fills by role (the hero's spec role); strangers at 10+ are spec'd.
    const party = kind === 'dungeon' ? dungeonParty(p, this.partySeed(mapId)) : [c.classId];
    const slot = Math.max(0, party.indexOf(c.classId));
    const { registry, error } = kind === 'dungeon' ? dungeonRegistry(p, this.host.content, party) : fieldRegistry(p, this.host.content, opts.trialSpecId ?? null);
    if (error) {
      console.error('[world] could not compile the hero; playing with the base class', error);
      kmToast('Could not apply your gear and talents (see console).', 'error', 5000);
    }
    const seed = ((p.seed >>> 0) + Math.imul(++this.loads, 0x9e3779b1)) >>> 0;
    const config: StartConfig = { slot, level: 0, preset: p.settings.defaultBotPreset, seed: seed % 1_000_000_000, dungeonId: mapId, affixes: [], renderer: this.host.settings().renderer };
    const factory = await this.host.rendererFactory(this.host.settings().renderer, registry);
    if (!current()) return;
    const names = party.map((id) => (id === c.classId ? c.name : (registry.class(id)?.name ?? id)));
    let session: Session;
    try {
      session = startSession({
        canvasHost: this.host.canvasHost,
        hudHost: this.host.hudHost,
        content: registry,
        party,
        partyNames: names,
        progress: null,
        config,
        onEnd: (end) => this.sessionEnded(end),
        createRenderer: factory,
        settings: this.host.settings(),
        looterId: `p${slot + 1}`,
        // Group Finder strangers in a dungeon (chat in companion mode); nobody in the fields.
        personas: kind === 'dungeon' ? safeCompanions(party, slot, config.seed, names) : null,
        field: {
          levels: party.map(() => level),
          chaseCamera: kind !== 'dungeon',
          ...(arrive && kind !== 'dungeon' ? { arrive: { pos: arrive.pos, facing: arrive.facing } } : {}),
          onEvents: (events, world) => this.onEvents(events, world),
          autoBrain: () => (this.auto.active ? (this.live?.brain ?? null) : null),
          onManual: (k) => {
            if (this.auto.manual(k)) this.syncAuto();
          },
        },
      });
    } catch (err) {
      console.error('[world] could not start the map', err);
      kmToast('Could not load this area (see console).', 'error', 6000);
      return;
    }
    const heroId = session.playerId ?? `p${slot + 1}`;
    const live: Live = {
      session,
      mapId,
      map,
      kind,
      registry,
      heroId,
      brain: null,
      batch: new KillBatch(),
      pickups: [],
      pickupXp: 0,
      pickupSince: null,
      timer: setInterval(() => this.upkeep(), UPKEEP_MS),
      lastSave: performance.now(),
      ...(opts.back ? { back: opts.back } : {}),
      area: null,
    };
    try {
      live.brain = createBot({ unitId: heroId, classId: c.classId, skill: 'competent', seed: config.seed ^ 0x5eed, grind: kind === 'field', wits: heroWits(this.store.profile) } as BotOptions & { grind?: boolean }); // +grid: wits
    } catch (err) {
      console.warn('[world] Auto brain unavailable', err);
    }
    this.live = live;
    this.auto.setMap(kind);
    this.gather.attach(session, mapId, heroId, kind === 'field' ? map.field?.gatherNodes : undefined, live.brain as GrindBrain | null); // +prof
    this.quests.attach(session, mapId, heroId, kind === 'dungeon' ? undefined : map.field?.npcs); // +quests
    // +presence: online, other players on this field / town are visible (never offline, in a dungeon or the tutorial).
    if (kind !== 'dungeon' && this.store.online && map.field) startFieldPresence({ zoneId: map.field.zoneId, fieldId: mapId, heroName: c.name, classId: c.classId, hudHost: this.host.hudHost, getLocalState: simLocalState(() => (this.live === live ? live.session.sim.getWorld() : null), heroId) });
    this.host.started(session);
    metaBridge.setOverlayNote(kind === 'dungeon' ? 'In a dungeon: gear and talent changes apply when you leave it.' : ''); // open world: no banner (it overlapped the header icons)
    const v = fieldView;
    v.active = true;
    v.mapKind = kind;
    v.mapId = mapId;
    v.mapName = map.name;
    v.band = map.field && kind === 'field' ? `Lv ${map.field.levelBand[0]}–${map.field.levelBand[1]}` : kind === 'dungeon' ? `Lv ${map.mobLevel ?? ''} · Normal` : '';
    v.heroName = c.name;
    v.classId = c.classId;
    v.actions = {
      toggleAuto: () => this.toggleAuto(),
      interact: (id) => this.interact(id),
      leaveDungeon: () => void this.leaveDungeon(false),
      trackQuest: (id) => this.quests.track(id), // +quests
      toggleQuestLog: () => this.quests.toggleLog(), // +quests
    };
    this.refresh();
    this.trackArea(live);
    this.syncAuto();
    this.fadeIn();
    const pending = this.after;
    this.after = [];
    for (const f of pending) f();
  }

  /** Re-read the profile into the HUD (XP, tracker, quest markers). */
  refresh(): void {
    const l = this.live;
    const p = this.store.profile;
    const c = p.character;
    if (!l || !c) return;
    const hero = p.characters[c.classId]?.hero;
    const level = hero?.level ?? 1;
    const v = fieldView;
    v.level = level;
    v.maxed = level >= LEVEL_CAP;
    v.next = v.maxed ? 0 : xpToNext(level);
    v.xp = v.maxed ? 0 : Math.min(v.next, (hero?.xp ?? 0) + l.batch.predicted + l.pickupXp);
    v.xpVersion++;
    // The skill cluster's crest reads the in-run view (level, gold under the crest).
    const r = inRunView;
    r.active = true;
    r.classId = c.classId;
    r.heroName = c.name;
    r.level = level;
    r.xp = v.xp;
    r.next = v.next;
    r.maxed = v.maxed;
    r.startXp = v.xp;
    r.gold = r.goldTarget = p.currencies.gold;
    r.version++;
    v.tracker = trackerRows(c, this.names(), level);
    if (l.kind !== 'dungeon') v.tracker.push(...this.quests.rows(level)); // +quests: side quests under the main quest
    if (l.kind === 'dungeon') v.tracker.unshift({ id: 'dungeon', kind: 'dungeon', title: l.map.name, text: 'Defeat the bosses with your group', cur: 0, max: 1, done: false });
    v.trackerVersion++;
    v.waypoint = this.quests.waypoint(level, waypointOn(c, l.mapId, this.host.content.bundle.dungeons)); // +quests: a tracked side quest
    v.waypointVersion++;
    v.autoHint = l.kind === 'field' && currentChainStep(c.bounties, c.zoneId)?.kind === 'auto';
    if (l.map.field?.npcs?.length) l.session.renderer.setNpcMarkers?.({ ...npcMarkers(c, level), ...this.quests.markers(level) }); // +quests
  }

  private names(): { npc(id: string): string | undefined; map(id: string): string | undefined; area(id: string): string | undefined } {
    const content = this.host.content;
    return {
      npc: (id) => content.npc(id)?.name ?? this.townNpc(id)?.name,
      map: (id) => content.dungeon(id)?.name,
      area: (id) => this.live?.map.field?.areas?.find((a) => a.id === id)?.name,
    };
  }

  // -------------------------------------------------------------------------
  // +zone: areas (name toast on entry, the tracker header, discovery XP, Auto's safe areas)
  // -------------------------------------------------------------------------

  /** Follow the area the hero stands in; on a change, update the header and announce it. */
  private trackArea(l: Live): void {
    const field = l.map.field;
    if (!field?.areas?.length || l.kind === 'dungeon') return;
    const u = l.session.sim.getWorld().units[l.heroId];
    if (!u) return;
    const area = fieldAreaAt(field, u.pos);
    const id = area?.id ?? null;
    if (id === l.area) return;
    const first = l.area === null;
    l.area = id;
    const region = area?.region ? (field.areas.find((a) => a.id === area.region) ?? null) : area;
    const v = fieldView;
    v.mapName = area ? area.name : l.map.name; // the area alone: "Area · Region" wrapped or truncated in the tracker header
    const band = area?.levelBand ?? region?.levelBand ?? field.levelBand;
    v.band = area?.safe ? 'Safe' : `Lv ${band[0]}–${band[1]}`;
    fieldView.trackerVersion++;
    this.auto.setSafe(!!area?.safe);
    if (area?.safe && this.auto.active) this.toggleAuto(false);
    this.syncAuto();
    if (!area) return;
    if (!first || !area.safe) zoneText(this.host.hudHost, area.name, area.region && region ? region.name : (area.safe ? 'Safe' : `Level ${band[0]}–${band[1]}`));
    // Discovery: the area (and its region, if new) pays once.
    const c = this.store.profile.character;
    const seen = new Set(c?.discovered ?? []);
    for (const a of [area, area.region ? region : null]) {
      if (!a || !(a.discoverKills ?? 0) || seen.has(`${l.mapId}:${a.id}`)) continue;
      void this.store.act('fieldDiscover', { mapId: l.mapId, areaId: a.id, pos: { x: round2(u.pos.x), y: round2(u.pos.y) } }).then((r) => {
        if (!r.ok) return;
        const g = r as unknown as FieldGain & { discovered: boolean; areaName: string; bonus: number };
        if (!g.discovered) return;
        kmToast(`Discovered: ${g.areaName}${g.bonus ? `  ·  +${g.bonus} XP` : ''}`, 'info', 3200);
        playSound('bounty.complete');
        if (this.live === l) {
          const hero = l.session.sim.getWorld().units[l.heroId];
          if (hero && g.bonus) rewardPops.current?.add(hero.pos, [{ kind: 'xp', amount: g.bonus, label: `Discovered ${g.areaName}` }]);
        }
        this.applyGain(g);
      });
    }
  }

  /** +glide: the one-time glide tip on the first jump from a high place; a toast on each sky puzzle's top. */
  private skyHints(events: readonly SimEvent[], world: DeepReadonly<World>, l: Live): void {
    const as = l.map.airspace;
    if (!as) return;
    if (jumpedFromHigh(events, world, l.heroId, as) && onceFlag('ks.tip.glide.v1')) kmToast(`${GLIDE_TIP} (release to drop)`, 'info', 4200);
    for (const pz of puzzleTopsReached(events, l.heroId, as)) {
      if (!onceFlag(`ks.sky.${l.mapId}.${pz.id}`)) continue;
      kmToast(`Sky puzzle found: ${pz.name}`, 'info', 3600);
      playSound('bounty.complete');
    }
  }

  /** The area at the hero (null off zone maps). */
  areaAtHero(): FieldAreaDef | null {
    const l = this.live;
    const u = l?.session.sim.getWorld().units[l.heroId];
    return l && u ? fieldAreaAt(l.map.field, u.pos) : null;
  }

  private townNpc(id: string): FieldNpcDef | undefined {
    for (const d of this.host.content.bundle.dungeons) {
      const n = d.field?.npcs?.find((x) => x.id === id);
      if (n) return n;
    }
    return undefined;
  }

  // -------------------------------------------------------------------------
  // Sim events: kills, portals, first casts of new abilities
  // -------------------------------------------------------------------------

  private onEvents(events: readonly SimEvent[], world: DeepReadonly<World>): void {
    const l = this.live;
    if (!l || !events.length) return;
    forwardSimEvents(events, world, l.heroId); // +presence: casts, swings, jumps, level-ups for the others
    this.skyHints(events, world, l); // +glide
    const kills = levelledKills(events, world);
    // Reward pops: every kill pops its XP and gold where it died (fields and the dungeon alike).
    if (kills.length && rewardPops.current) {
      const lvl = fieldView.level;
      for (const e of events) {
        if (e.type !== 'death') continue;
        const u = world.units[e.targetID];
        if (!u || u.faction !== 'enemy' || !u.npcId || typeof u.level !== 'number') continue;
        const xp = fieldView.maxed ? 0 : killXp(u.level, lvl, !!u.elite);
        rewardPops.current.add(u.pos, [{ kind: 'xp', amount: xp }, { kind: 'gold', amount: killGold(u.level, !!u.elite) }]);
      }
    }
    this.onPickups(l, events);
    // +grid: a key-bearing secret chest pays a Grid Key (server-checked).
    const keySecrets = keySecretsOpened(events, l.heroId, l.mapId, this.store.profile);
    if (keySecrets.length) claimSecretKeys(this.store, world, l.heroId, l.mapId, keySecrets, (held) => this.gridKeyBanner(held));
    this.gather.onEvents(events, world); // +prof
    if (kills.length) {
      const lvl = fieldView.level;
      // Auto earns the same XP; its kills go in their own batches (they advance the Auto step).
      const auto = this.auto.active;
      if (l.batch.mixes(auto)) this.flushKills(l);
      for (const k of kills) l.batch.add(k, lvl, performance.now(), auto);
      const hero = this.store.profile.characters[this.store.profile.character?.classId ?? '']?.hero;
      if (!fieldView.maxed) {
        fieldView.xp = Math.min(fieldView.next, (hero?.xp ?? 0) + l.batch.predicted + l.pickupXp);
        fieldView.xpVersion++;
      }
      if (l.batch.due(performance.now(), hero?.xp ?? 0, fieldView.next)) this.flushKills(l);
    }
    for (const e of events) {
      if (e.type === 'portal' && e.sourceID === l.heroId) this.onPortal(e.portalId);
      else if (e.type === 'waystone' && e.sourceID === l.heroId) {
        // +zone: arrived by waystone: save the new place and greet the area.
        this.savePosition();
        playSound('bounty.complete');
      }
      else if (e.type === 'cast' && e.sourceID === l.heroId && fieldView.newUnlocks.size) {
        const id = (e as { abilityGameID?: string }).abilityGameID;
        if (id && fieldView.newUnlocks.delete(id)) fieldView.unlockVersion++;
      }
    }
  }

  /** Ground pickups the hero collected this step: pop them, predict the XP, queue `fieldPickups`. */
  private onPickups(l: Live, events: readonly SimEvent[]): void {
    let got = false;
    for (const e of events) {
      if (e.type !== 'pickup' || e.sourceID !== l.heroId) continue;
      const kind = e.resource === 'xp' ? 'xp' : e.resource === 'gold' ? 'gold' : e.resource === 'item' ? 'item' : null;
      if (!kind) continue;
      got = true;
      l.pickups.push({ kind, amount: e.amount, ...(kind === 'item' ? { rarity: e.rarity } : {}) });
      l.pickupSince ??= performance.now();
      if (kind === 'xp' && !fieldView.maxed) l.pickupXp += e.amount;
      const chain = (e as { chain?: number }).chain ?? 0;
      rewardPops.current?.add(e.pos, [{ kind, amount: e.amount, ...(kind === 'item' ? { rarity: e.rarity as RewardRarity } : {}), ...(kind === 'xp' && chain > 1 ? { label: `Wisp chain ×${chain}` } : {}) }]);
      if (kind === 'xp' && chain > 1) playSound('reward.xp', { pitch: 1 + Math.min(0.6, 0.08 * chain) });
    }
    if (!got) return;
    if (!fieldView.maxed) {
      const hero = this.store.profile.characters[this.store.profile.character?.classId ?? '']?.hero;
      fieldView.xp = Math.min(fieldView.next, (hero?.xp ?? 0) + l.batch.predicted + l.pickupXp);
      fieldView.xpVersion++;
      // A level-up (or a gear drop) goes out at once; the rest rides the next flush.
      if ((hero?.xp ?? 0) + l.batch.predicted + l.pickupXp >= fieldView.next || l.pickups.some((q) => q.kind === 'item')) this.flushPickups(l, true);
    }
  }

  private flushPickups(l: Live, force = false): void {
    if (!l.pickups.length || (!force && (l.pickupSince === null || performance.now() - l.pickupSince < 1500))) return;
    if (l.batch.size) this.flushKills(l);
    const pickups = l.pickups;
    l.pickups = [];
    l.pickupSince = null;
    const mapId = l.mapId;
    void this.store.act('fieldPickups', { mapId, pickups }).then((r) => {
      if (this.live === l) l.pickupXp = 0;
      if (!r.ok) {
        console.warn('[world] pickups not accepted:', r.reason);
        this.refresh();
        return;
      }
      const g = r as unknown as FieldGain & { items: Item[]; equipped: boolean };
      for (const it of g.items ?? []) kmToast(`New gear: ${itemName(it)}${g.equipped ? ' (equipped)' : ''}`, 'info', 3200);
      this.applyGain(g);
    });
  }

  private flushKills(l: Live): void {
    if (!l.batch.size) return;
    const { kills, auto } = l.batch.drain();
    void this.store.act('fieldKills', { mapId: l.mapId, kills, ...(auto ? { auto: true } : {}) }).then((r) => {
      if (!r.ok) {
        console.warn('[world] kills not accepted:', r.reason);
        this.refresh();
        return;
      }
      this.applyGain(r as unknown as FieldGain);
    });
  }

  /** XP / quest results from any field intent: level-ups, finished steps, then a HUD refresh. */
  private applyGain(g: FieldGain, lead?: string): void {
    const before = fieldView.level;
    if (g.gridKeys?.length) this.gridKeyBanner(gridKeys(this.store.profile).held); // +grid: a rare's key
    this.quests.onGain(g); // +quests: kill progress, collected items
    for (const q of g.completed ?? []) if (q.kind === 'chain') this.questBanner(q);
    this.refresh();
    if (g.levels > 0) this.levelUp(before, g.level, lead);
  }

  /** +grid: a Grid Key found (secret chest, rare kill). */
  private gridKeyBanner(held: number): void {
    celebrations.current?.small({ title: 'GRID KEY FOUND', subtitle: 'A sealed sigil on the Waystone Grid can be opened (N)', lines: [`${held} key${held === 1 ? '' : 's'} held`] });
    playSound('bounty.complete');
  }

  private questBanner(q: QuestCompletion, title = q.kind === 'bounty' ? 'BOUNTY COMPLETE' : 'QUEST COMPLETE'): void {
    const lines = [q.reward.xp ? `+${q.reward.xp} XP` : '', q.reward.gold ? `+${q.reward.gold} gold` : ''].filter(Boolean);
    celebrations.current?.small({ title, subtitle: q.title, lines });
    playSound('bounty.complete');
    const l = this.live;
    if (l) l.session.renderer.celebrate?.(l.heroId, 'small');
    presenceEvent({ k: 'emote', a: 'cheer' }); // +presence: the others see the cheer
  }

  /** The LEVEL UP moment: sim setLevel (stats + world VFX), banner, sound, new-ability glow. */
  private levelUp(from: number, to: number, lead?: string): void {
    const l = this.live;
    const c = this.store.profile.character;
    if (!l || !c || to <= from) return;
    l.session.command({ type: 'setLevel', actorId: l.heroId, level: to });
    const sum = levelUpSummary(l.registry, c.classId, from, to);
    for (const a of sum.abilities) fieldView.newUnlocks.add(a);
    fieldView.unlockVersion++;
    const s = this.host.settings();
    celebrations.current?.levelUp({ from, to, gains: sum.gains, unitId: l.heroId, flash: s.screenShake && s.fxIntensity > 0.25, ...(lead ? { lead } : {}) });
    playSound('level.up');
    playSound('level.impact');
    l.session.renderer.celebrate?.(l.heroId, 'big');
  }

  private onPortal(portalId: string): void {
    const l = this.live;
    if (!l || this.auto.suppressPortal()) return;
    const portal = l.map.field?.portals.find((x) => x.id === portalId);
    if (!portal) return;
    if (portal.kind === 'dungeon') return this.dungeonPrompt(portal);
    this.travel(portal.to, portal.toPortal ?? null);
  }

  /** Walk through a portal: save the destination (completes 'goto' steps) and load it. */
  travel(mapId: string, toPortal: string | null): void {
    const arrive = this.arrivalAt(mapId, toPortal);
    void this.store.act('fieldMove', { mapId, pos: arrive?.pos ?? null, facing: arrive?.facing ?? 0 }).then((r) => {
      if (r.ok) {
        const g = r as unknown as FieldGain;
        // Shown on the new map (a 'goto' step finishes on arrival).
        if (g.completed?.length || g.levels > 0) this.after.push(() => this.applyGain(g));
      }
    });
    this.store.flush();
    void this.load(mapId, arrive);
  }

  /** In front of `portalId` on `mapId` (its mouth faces the way arrivals step out). */
  arrivalAt(mapId: string, portalId: string | null): Arrive {
    const map = this.host.content.dungeon(mapId);
    const p = portalId ? map?.field?.portals.find((x) => x.id === portalId) : undefined;
    if (!p) return null;
    const d = p.radius + 2.5;
    return { pos: { x: round2(p.pos.x + Math.cos(p.facing) * d), y: round2(p.pos.y + Math.sin(p.facing) * d) }, facing: p.facing };
  }

  // -------------------------------------------------------------------------
  // Upkeep: Auto status, talk prompt, kill flush, periodic save
  // -------------------------------------------------------------------------

  private upkeep(): void {
    const l = this.live;
    if (!l) return;
    const now = performance.now();
    const w = l.session.sim.getWorld();
    const u = w.units[l.heroId];
    if (l.batch.due(now, this.store.profile.characters[this.store.profile.character?.classId ?? '']?.hero.xp ?? 0, fieldView.next)) this.flushKills(l);
    this.flushPickups(l);
    let st = this.auto.status(u ? { alive: u.alive, inCombat: u.inCombat, hp: u.hp, maxHp: u.maxHp } : null);
    if (st === 'hunting' && this.gather.autoGathering()) st = 'gathering'; // +prof
    if (st !== fieldView.auto.status) {
      fieldView.auto.status = st;
      fieldView.autoVersion++;
    }
    // Talk prompt: the nearest town NPC in reach.
    let near: FieldNpcDef | null = null;
    if (u?.alive && l.map.field?.npcs?.length) {
      let best = TALK_RANGE;
      for (const n of l.map.field.npcs) {
        const d = dist(n.pos, u.pos);
        if (d <= best) {
          best = d;
          near = n;
        }
      }
    }
    const cur = fieldView.prompt?.npcId ?? null;
    const next = near && !townPanelOpen() && !this.host.router.isOverlay ? near : null;
    // +prof: no NPC in reach: the nearest node the hero can gather ("[T] Gather Sunleaf").
    const node = !next && !townPanelOpen() && !this.host.router.isOverlay && !this.auto.active ? this.gather.prompt(w) : null;
    if ((next?.id ?? node?.npcId ?? null) !== cur || (node && node.title !== fieldView.prompt?.title)) {
      fieldView.prompt = next ? { npcId: next.id, name: next.name, title: next.title ?? '', ...(next.role === 'waystone' ? { verb: 'use' as const } : {}) } : node;
      fieldView.promptVersion++;
    }
    this.gather.upkeep(w); // +prof
    this.quests.upkeep(u?.alive ? u.pos : null); // +quests: barks
    if (l.kind !== 'dungeon' && now - l.lastSave >= SAVE_MS) this.savePosition();
    this.trackArea(l);
    this.waystoneUpkeep(l, now);
  }

  // -------------------------------------------------------------------------
  // +zone: waystones
  // -------------------------------------------------------------------------

  /** Attune stones the hero walks up to; advance / cancel a travel channel. */
  private waystoneUpkeep(l: Live, now: number): void {
    const u = l.session.sim.getWorld().units[l.heroId];
    const c = this.store.profile.character;
    if (!u || !c) return;
    const attuned = new Set(c.waystones ?? []);
    for (const n of l.map.field?.npcs ?? []) {
      if (n.role !== 'waystone' || attuned.has(`${l.mapId}:${n.id}`) || dist(n.pos, u.pos) > WAYSTONE_ATTUNE_NEAR) continue;
      const busy = (l.attuning ??= new Set());
      if (busy.has(n.id)) continue;
      busy.add(n.id);
      void this.store.act('waystoneAttune', { mapId: l.mapId, waystoneId: n.id, pos: { x: round2(u.pos.x), y: round2(u.pos.y) } }).then((r) => {
        busy.delete(n.id);
        if (!r.ok || !(r as unknown as { attuned: boolean }).attuned) return;
        kmToast(`Waystone attuned: ${n.title ?? n.name}`, 'info', 2600);
        playSound('bounty.complete');
      });
    }
    const ch = l.channel;
    if (!ch) return;
    const hurt = u.hp < ch.hp - 0.5;
    if (!u.alive || u.inCombat || hurt || dist(u.pos, ch.pos) > 0.6) return this.endChannel(l, 'Travel interrupted.');
    if (now - ch.at < WAYSTONE_CHANNEL_MS) return;
    this.endChannel(l, null);
    l.session.command({ type: 'interact', actorId: l.heroId, what: `waystone:${ch.to}` });
  }

  private endChannel(l: Live, why: string | null): void {
    l.channel?.el.remove();
    l.channel = null;
    if (why) kmToast(why, 'info', 1600);
  }

  /** The waystone panel: the attuned others, Travel (channel) or the cooldown left. */
  private waystonePanel(npc: FieldNpcDef): void {
    const l = this.live;
    const c = this.store.profile.character;
    if (!l || !c) return;
    const w = l.session.sim.getWorld();
    const readyAt = w.field?.waystoneReadyAt?.[l.heroId] ?? 0;
    const wait = Math.max(0, Math.ceil((readyAt - w.time) / 1000));
    const panel = openTownPanel(this.host.hudHost, { title: npc.title ?? 'Waystone', subtitle: 'Waystone', className: 'fx-board' });
    const attuned = new Set(c.waystones ?? []);
    const stones = (l.map.field?.waystones ?? []).filter((s) => s.id !== npc.id);
    el('p', 'fx-say', panel.body, 'The stone hums under your hand. Pick an attuned waystone to travel there. Waystones you have not reached yet stay dark.');
    for (const s of stones) {
      const on = attuned.has(`${l.mapId}:${s.id}`);
      const box = el('div', 'fx-quest' + (on ? '' : ' locked'), panel.body);
      const row = el('div', 'fx-quest-row', box);
      el('span', 'fx-quest-title', row, s.name);
      const far = Math.round(dist(s.pos, npc.pos));
      el('span', 'fx-quest-text', row, on ? `${far} yd away` : 'Not attuned');
      if (!on) continue;
      if (wait > 0) {
        el('span', 'fx-quest-text', row, `Ready in ${wait} s`);
        continue;
      }
      btn(row, 'Travel', () => {
        closeTownPanel();
        this.startChannel(s.id, s.name);
      });
    }
    const bye = el('button', 'fx-btn ghost', panel.foot, 'Goodbye');
    bye.type = 'button';
    bye.addEventListener('click', () => closeTownPanel());
  }

  private startChannel(to: string, name: string): void {
    const l = this.live;
    const u = l?.session.sim.getWorld().units[l.heroId];
    if (!l || !u?.alive) return;
    if (u.inCombat) return void kmToast('You cannot travel while in combat.', 'error', 1800);
    if (this.auto.active) this.toggleAuto(false);
    l.channel?.el.remove();
    const box = el('div', 'fx-channel', this.host.hudHost);
    box.style.cssText = 'position:absolute;left:50%;bottom:24%;transform:translateX(-50%);width:240px;pointer-events:none;z-index:40;text-align:center;font-size:13px;color:#f3ead2;text-shadow:0 1px 3px rgba(0,0,0,0.85);';
    el('div', '', box, `Travelling to ${name}…`);
    const track = el('div', '', box);
    track.style.cssText = 'margin-top:4px;height:8px;border-radius:4px;background:rgba(10,14,20,0.7);border:1px solid rgba(255,230,170,0.45);overflow:hidden;';
    const bar = el('div', '', track);
    bar.style.cssText = `height:100%;width:0%;background:linear-gradient(90deg,#7fd6ff,#d9f4ff);transition:width ${WAYSTONE_CHANNEL_MS}ms linear;`;
    requestAnimationFrame(() => requestAnimationFrame(() => (bar.style.width = '100%')));
    l.channel = { to, name, at: performance.now(), pos: { x: u.pos.x, y: u.pos.y }, hp: u.hp, el: box };
    playSound('ui.click');
  }

  // -------------------------------------------------------------------------
  // Auto
  // -------------------------------------------------------------------------

  toggleAuto(force?: boolean): void {
    if (force !== undefined && force === this.auto.active) return;
    const on = this.auto.toggle();
    if (!on && this.auto.reason) kmToast(this.auto.reason, 'info', 2200);
    if (on) closeTownPanel();
    // +zone: on a big map Auto hunts around where it was turned on (this region), never across the zone.
    const l = this.live;
    const brain = l?.brain as GrindBrain | null | undefined;
    if (on && l && brain?.setGrindHome) {
      const u = l.session.sim.getWorld().units[l.heroId];
      const zoned = !!l.map.field?.areas?.length;
      brain.setGrindHome(zoned && u ? { pos: { x: u.pos.x, y: u.pos.y }, radius: AUTO_HOME_YD, region: fieldRegionAt(l.map.field, u.pos)?.id ?? null } : null);
    }
    this.syncAuto();
  }

  private syncAuto(): void {
    const a = fieldView.auto;
    a.on = this.auto.active;
    a.available = this.auto.available;
    const l = this.live;
    const u = l ? l.session.sim.getWorld().units[l.heroId] : undefined;
    a.status = this.auto.status(u ? { alive: u.alive, inCombat: u.inCombat, hp: u.hp, maxHp: u.maxHp } : null);
    fieldView.autoVersion++;
  }

  // -------------------------------------------------------------------------
  // Town NPCs
  // -------------------------------------------------------------------------

  private canvasClick(e: PointerEvent): void {
    const l = this.live;
    const d = this.downAt;
    this.downAt = null;
    if (!l || e.button !== 0 || !d || Math.hypot(e.clientX - d.x, e.clientY - d.y) > 6) return;
    const id = l.session.renderer.npcAt?.(e.clientX, e.clientY) ?? null;
    if (!id) return;
    const npc = l.map.field?.npcs?.find((n) => n.id === id);
    const u = l.session.sim.getWorld().units[l.heroId];
    if (!npc || !u) return;
    if (dist(npc.pos, u.pos) > CLICK_RANGE) return void kmToast(`Walk closer to ${npc.name}.`, 'info', 1800);
    this.interact(id);
  }

  interact(npcId: string): void {
    if (npcId.startsWith('gather:')) return this.gather.interact(npcId); // +prof
    const l = this.live;
    const npc = l?.map.field?.npcs?.find((n) => n.id === npcId);
    if (!l || !npc) return;
    if (this.auto.active) this.toggleAuto(false);
    // +zone: the server checks "in town" against the saved position: save it first (queued before the panel's intents).
    this.savePosition();
    // +spawn: a side-quest NPC who is also the main quest's current talk step (Granny Pell) speaks for the main quest first.
    const cur = this.store.profile.character ? currentChainStep(this.store.profile.character.bounties, this.store.profile.character.zoneId) : null;
    if (cur?.kind === 'talk' && cur.npcId === npc.id) return this.elderPanel(npc);
    if (this.quests.handles(npc.id)) return this.quests.open(npc); // +quests: side-quest NPCs
    const classId = this.store.profile.character?.classId ?? this.store.profile.mainClassId;
    switch (npc.role) {
      case 'questgiver':
        return this.elderPanel(npc);
      case 'bounty_board':
        return this.boardPanel(npc);
      case 'waystone':
        return this.waystonePanel(npc);
      case 'forge':
        return this.overlay(ROUTES.forge, { classId });
      case 'gear':
      case 'vendor':
        return this.overlay(ROUTES.hero, { classId, tab: 'gear' });
      case 'dungeon_board': {
        // +zone: keystones wait until the Hollow Barrow is cleared (the main quest comes first).
        const c = this.store.profile.character;
        if (c && !dungeonBoardOpen(c.bounties, c.zoneId)) return this.flavourPanel(npc, 'Keystones are for seasoned adventurers. Clear the Hollow Barrow first, then come and see what deeper places await.');
        return this.overlay(ROUTES.dungeons, { tab: 'mplus' });
      }
      default:
        return this.flavourPanel(npc);
    }
  }

  private overlay(route: string, params: Record<string, unknown>): void {
    closeTownPanel();
    this.host.router.openOverlay(route, params);
  }

  /** Gear / talents changed in an overlay: reload the hero where it stands (fields and town). */
  private overlayChanged(): void {
    const r = this.host.router;
    const open = r.isOverlay;
    if (open && !this.wasOverlay) {
      this.overlaySnapshot = this.heroBuild();
    } else if (!open && this.wasOverlay) {
      const l = this.live;
      if (l && l.kind !== 'dungeon' && this.overlaySnapshot !== null && this.overlaySnapshot !== this.heroBuild()) {
        const u = l.session.sim.getWorld().units[l.heroId];
        const arrive = u ? { pos: { x: u.pos.x, y: u.pos.y }, facing: u.facing } : null;
        this.savePosition();
        void this.load(l.mapId, arrive);
      }
      this.overlaySnapshot = null;
    }
    this.wasOverlay = open;
  }

  private heroBuild(): string {
    const p = this.store.profile;
    const ch = p.character ? p.characters[p.character.classId] : undefined;
    return ch ? JSON.stringify([ch.gear, ch.activeLoadoutId, ch.loadouts, ch.grid?.nodes, ch.hero.specId]) : ''; // +grid: grid stars reload the hero too
  }

  /** A quest giver (Elder Rowan, the Hearthkeeper, the Tower Warden): the main quest's talk steps. */
  private elderPanel(npc: FieldNpcDef): void {
    const p = this.store.profile;
    const c = p.character;
    if (!c) return;
    const step = currentChainStep(c.bounties, c.zoneId);
    const mine = step?.kind === 'talk' && step.npcId === npc.id;
    const special = mine && step.panel ? step.panel : null;
    const panel = openTownPanel(this.host.hudHost, { title: npc.name, subtitle: npc.title ?? 'Village Elder', ...(special ? { className: `fx-story fx-story-${special}` } : {}) });
    const say = (t: string): void => void el('p', 'fx-say', panel.body, t);
    if (mine) {
      say(QUEST_LINES[step.id]?.(c.name) ?? `${c.name}, I have word for you.`);
      if (special === 'idle_intro') idleIntroGraphic(panel.body);
      questBox(panel.body, step.title, null, step.reward, false);
      const label = special === 'advancement' ? 'Begin the Trial' : special === 'idle_intro' ? 'Got it' : step.id === zoneQuests(c.zoneId)?.chain[0]?.id ? 'Accept' : 'Complete';
      const go = el('button', 'fx-btn', panel.foot, label);
      go.type = 'button';
      go.addEventListener('click', () => {
        go.disabled = true;
        void this.store.act('bountyTalk', { npcId: npc.id }).then((r) => {
          if (!r.ok) return void kmToast(r.reason, 'error');
          panel.close();
          const g = r as unknown as FieldGain;
          this.applyGain(g);
          if (special === 'advancement') this.advancementPanel(npc); // +specs: straight into the Trial
        });
      });
    } else if (npc.id === 'tower_warden' && heroLevelOf(p) >= SPEC_LEVEL) {
      // +specs: after the main quest, the Warden always runs the Advancement Trial (and respecs).
      panel.close();
      return this.advancementPanel(npc);
    } else if (step) {
      say(npc.id === 'elder_rowan' ? ELDER_NUDGE(c.name) : IDLE_LINES[npc.id] ?? 'Safe travels, friend.');
      const t = trackerRows(c, this.names(), heroLevelOf(p))[0];
      questBox(panel.body, step.title, t ? `${t.text}${t.max > 1 ? ` (${t.cur}/${t.max})` : ''}` : null, step.reward, false);
    } else {
      say(npc.id === 'tower_warden' ? `Grow strong, ${c.name}. At level ${SPEC_LEVEL} the Trial is yours.` : `Mossvale breathes easier thanks to you, ${c.name}. The Bounty Board always has work, and the Tower waits beyond Bramblewood.`);
      if (npc.id === 'hearthkeeper') idleIntroGraphic(panel.body);
    }
    const bye = el('button', 'fx-btn ghost', panel.foot, 'Goodbye');
    bye.type = 'button';
    bye.addEventListener('click', () => panel.close());
  }

  // -------------------------------------------------------------------------
  // +specs: the Advancement Trial (docs/design/classes/specs.md)
  // -------------------------------------------------------------------------

  /** Warden Ilsa's panel: the three spec cards of the hero's class, Try (30 s) and Choose. */
  advancementPanel(npc: FieldNpcDef): void {
    const p = this.store.profile;
    const c = p.character;
    if (!c) return;
    const level = heroLevelOf(p);
    const current = heroSpecId(p);
    const info = specInfo(current);
    const panel = openTownPanel(this.host.hudHost, { title: npc.name, subtitle: 'The Advancement Trial', className: 'fx-trial' });
    el(
      'p',
      'fx-say',
      panel.body,
      info
        ? `Walking the ${info.name}'s path suits you, ${c.name}. If another calls to you, I can retrain you for ${RESPEC_GOLD} gold.`
        : level < SPEC_LEVEL
          ? `Not yet, ${c.name}. Reach level ${SPEC_LEVEL} and the Trial is yours.`
          : `${c.name}, every warrior who climbs the Tower chooses a path first. Each one remakes every skill you know on the same keys. Try them on my practice dummy, then choose.`,
    );
    specCards(panel.body, {
      classId: c.classId,
      current,
      level,
      needLevel: SPEC_LEVEL,
      gold: p.currencies.gold,
      respecGold: RESPEC_GOLD,
      content: this.host.content,
      onTry: (id) => {
        panel.close();
        this.tryTrial(id, npc.id);
      },
      onChoose: (id) => {
        panel.close();
        void this.chooseSpec(id);
      },
    });
    const bye = el('button', 'fx-btn ghost', panel.foot, 'Goodbye');
    bye.type = 'button';
    bye.addEventListener('click', () => panel.close());
  }

  /** "Try (30 s)": reload where the hero stands with the spec compiled in (local only), then come back. */
  tryTrial(specId: string, npcId = 'tower_warden'): void {
    const l = this.live;
    const info = specInfo(specId);
    if (!l || !info || l.kind === 'dungeon') return;
    if (this.auto.active) this.toggleAuto(false);
    const u = l.session.sim.getWorld().units[l.heroId];
    const arrive = u ? { pos: { x: u.pos.x, y: u.pos.y }, facing: u.facing } : null;
    this.clearTrial();
    const banner = el('div', 'fx-trial-banner', this.host.hudHost);
    banner.style.setProperty('--spec-tint', info.ui.tint);
    banner.style.setProperty('--spec-glow', info.ui.glow);
    el('span', '', banner, `Trial: ${info.name}`);
    const t = el('span', 't', banner, '30');
    const end = el('button', 'fx-btn ghost', banner, 'End');
    end.type = 'button';
    end.addEventListener('click', () => this.endTrial(true));
    const until = performance.now() + TRIAL_MS;
    const timer = setInterval(() => {
      const left = Math.max(0, Math.ceil((until - performance.now()) / 1000));
      t.textContent = String(left);
      if (left <= 0) this.endTrial(true);
    }, 250);
    this.trial = { specId, until, timer, banner, npcId };
    this.after.push(() => kmToast(`Trying ${info.name} for 30 seconds: hit the practice dummy beside Warden Ilsa.`, 'info', 3600));
    void this.load(l.mapId, arrive, { trialSpecId: specId });
  }

  private clearTrial(): void {
    const t = this.trial;
    if (!t) return;
    clearInterval(t.timer);
    t.banner.remove();
    this.trial = null;
  }

  /** End the preview: reload with the real build; `reopen` brings Warden Ilsa's panel back. */
  private endTrial(reopen: boolean): void {
    const t = this.trial;
    const l = this.live;
    if (!t) return;
    this.clearTrial();
    if (!l) return;
    const u = l.session.sim.getWorld().units[l.heroId];
    const arrive = u ? { pos: { x: u.pos.x, y: u.pos.y }, facing: u.facing } : null;
    if (reopen) {
      this.after.push(() => {
        const npc = this.live?.map.field?.npcs?.find((n) => n.id === t.npcId);
        if (npc) this.advancementPanel(npc);
      });
    }
    void this.load(l.mapId, arrive);
  }

  /** Commit (server-authoritative `chooseSpec`), reload with the spec compiled in, then celebrate. */
  async chooseSpec(specId: string): Promise<void> {
    this.savePosition();
    const r = await this.store.act('chooseSpec', { specId });
    if (!r.ok) return void kmToast(r.reason, 'error', 4000);
    const res = r as unknown as { previous: string | null; cost: number };
    this.store.flush();
    const l = this.live;
    if (!l) return;
    const u = l.session.sim.getWorld().units[l.heroId];
    const arrive = u ? { pos: { x: u.pos.x, y: u.pos.y }, facing: u.facing } : null;
    this.after.push(() => this.specCelebration(specId, res.previous, res.cost));
    void this.load(l.mapId, arrive);
  }

  /** The big moment: the spec's name over the hero, its tint, the bar sliding in, a toast per evolved ability. */
  private specCelebration(specId: string, previous: string | null, cost: number): void {
    const l = this.live;
    const info = specInfo(specId);
    const c = this.store.profile.character;
    if (!l || !info || !c) return;
    const lines = evolvedLines(this.host.content, this.host.content.class(c.classId), specId);
    const s = this.host.settings();
    celebrations.current?.levelUp({
      from: heroLevelOf(this.store.profile),
      to: heroLevelOf(this.store.profile),
      word: info.name.toUpperCase(),
      label: previous ? 'A new path' : 'Specialisation',
      value: info.role === 'tank' ? 'Tank' : info.role === 'healer' ? 'Healer' : 'Damage',
      tint: info.ui.tint,
      lead: info.ui.tagline,
      // The banner shows the first three evolutions and the new buttons; the toasts list all of them.
      gains: [
        ...lines.filter((x) => x.from).slice(0, 3).map((x) => ({ kind: 'ability' as const, text: `${x.from} → ${x.to}`, abilityId: x.abilityId })),
        ...lines.filter((x) => !x.from).map((x) => ({ kind: 'ability' as const, text: `New: ${x.to} (${x.key})`, abilityId: x.abilityId })),
      ],
      unitId: l.heroId,
      flash: s.screenShake && s.fxIntensity > 0.25,
    });
    playSound('level.up');
    playSound('level.impact');
    l.session.renderer.celebrate?.(l.heroId, 'big');
    presenceEvent({ k: 'emote', a: 'cheer' }); // +presence
    // The new action bar slides in.
    const bar = this.host.hudHost.querySelector('.hkc-main');
    if (bar) {
      bar.classList.remove('hk-spec-in');
      void (bar as HTMLElement).offsetWidth;
      bar.classList.add('hk-spec-in');
    }
    // A toast for every evolved ability, and one for each spec-only button.
    // After the banner, so they never stack on its title.
    const T0 = 5200;
    lines.forEach((x, i) => setTimeout(() => kmToast(x.from ? `${x.from} evolved into ${x.to} (key ${x.key})` : `New: ${x.to} (key ${x.key})`, 'info', 3800), T0 + i * 450));
    if (cost) setTimeout(() => kmToast(`Retrained for ${cost} gold.`, 'info', 3000), T0 + lines.length * 450);
    for (const x of lines) fieldView.newUnlocks.add(x.abilityId);
    fieldView.unlockVersion++;
  }

  /** +specs: the Group Finder seed of a dungeon visit (same in the prompt and the run). */
  private partySeed(dungeonId: string): number {
    const p = this.store.profile;
    const clears = p.character?.dungeonClears?.[dungeonId] ?? 0;
    let h = (Math.imul((p.seed >>> 0) ^ 0x5bd1e995, 31) + clears * 7919 + dungeonId.length * 131) >>> 0;
    h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d) >>> 0;
    return (h % 1_000_000) + 1;
  }

  private boardPanel(npc: FieldNpcDef): void {
    const c0 = this.store.profile.character;
    if (c0) {
      // +zone: the main quest's "Bounties by the Well" step completes here, unlocking the board.
      const step = currentChainStep(c0.bounties, c0.zoneId);
      if (step?.kind === 'talk' && step.npcId === npc.id) {
        void this.store.act('bountyTalk', { npcId: npc.id }).then((r) => {
          if (r.ok) this.applyGain(r as unknown as FieldGain);
          this.boardPanel(npc);
        });
        return;
      }
      if (!boardUnlocked(c0.bounties, c0.zoneId)) return this.flavourPanel(npc, 'Old notices, faded by the rain. Nothing here for you yet. Come back when you have proven yourself.');
    }
    const render = (): void => {
      const p = this.store.profile;
      const c = p.character;
      const z = c ? zoneQuests(c.zoneId) : undefined;
      if (!c || !z) return;
      const level = heroLevelOf(p);
      const panel = openTownPanel(this.host.hudHost, { title: npc.name, subtitle: 'Repeatable bounties', className: 'fx-board' });
      // +zone: each board posts its own bounties (the town's, the Lakeside Outpost's).
      for (const b of z.bounties.filter((x) => (x.boardNpcId ?? z.boardNpcId) === npc.id)) {
        const have = c.bounties.active[b.id];
        const ready = have !== undefined && have >= b.count;
        const locked = have === undefined && (b.minLevel ?? 1) > level;
        const done = c.bounties.completed[b.id] ?? 0;
        const mobs = b.npcIds.map((id) => this.host.content.npc(id)?.name ?? id).join(' or ');
        const map = this.host.content.dungeon(b.mapId);
        const where = (b.areaId ? map?.field?.areas?.find((a) => a.id === b.areaId)?.name : undefined) ?? map?.name ?? b.mapId;
        const box = questBox(panel.body, b.title, `Slay ${b.count} ${mobs} in ${where}.`, b.reward, ready, `${have !== undefined ? `${Math.min(have, b.count)}/${b.count}` : locked ? `Level ${b.minLevel}` : done ? `Done ×${done}` : `Lv ${b.minLevel ?? 1}+`}`);
        box.classList.toggle('locked', locked);
        const row = el('div', 'fx-quest-row', box);
        el('span', 'fx-quest-text', row, have === undefined ? (locked ? 'Too dangerous for you yet.' : 'Available') : ready ? 'Ready to turn in!' : 'In progress');
        if (have === undefined && !locked) btn(row, 'Accept', () => this.store.act('bountyAccept', { bountyId: b.id }).then((r) => (r.ok ? (this.refresh(), render()) : kmToast(r.reason, 'error'))));
        if (ready) {
          btn(row, 'Turn in', () =>
            this.store.act('bountyTurnIn', { bountyId: b.id }).then((r) => {
              if (!r.ok) return kmToast(r.reason, 'error');
              const g = r as unknown as FieldGain;
              const q: QuestCompletion = { kind: 'bounty', id: b.id, title: b.title, reward: b.reward };
              if (g.levels > 0) {
                // One celebration: the level-up, led by the bounty.
                this.refresh();
                this.levelUp(fieldView.level - g.levels, g.level, `Bounty complete: ${b.title}  ·  +${b.reward.xp} XP  ·  +${b.reward.gold} gold`);
                playSound('bounty.complete');
              } else {
                this.questBanner(q);
                this.applyGain(g);
              }
              render();
            }),
          );
        }
      }
    };
    render();
  }

  private flavourPanel(npc: FieldNpcDef, line?: string): void {
    const panel = openTownPanel(this.host.hudHost, { title: npc.name, ...(npc.title ? { subtitle: npc.title } : {}) });
    el('p', 'fx-say', panel.body, line ?? (npc.role === 'lore' ? (LORE_LINES[npc.id] ?? 'The carving is too worn to read.') : npc.role === 'guard' ? 'Keep your blade sharp. The bandits in Bramblewood grow bolder every week.' : (VILLAGER_LINES[npc.id] ?? 'Fine weather for an adventure, isn’t it?')));
    const b = el('button', 'fx-btn ghost', panel.foot, 'Goodbye');
    b.type = 'button';
    b.addEventListener('click', () => panel.close());
  }

  // -------------------------------------------------------------------------
  // The levelled dungeon (Group Finder)
  // -------------------------------------------------------------------------

  private dungeonPrompt(portal: PortalDef): void {
    const l = this.live;
    const d = this.host.content.dungeon(portal.to);
    const c = this.store.profile.character;
    if (!l || !d || !c) return;
    const level = heroLevelOf(this.store.profile);
    const mob = d.mobLevel ?? portal.minLevel ?? 1;
    const lo = portal.minLevel ?? Math.max(1, mob - 1);
    const hi = mob + 2;
    const panel = openTownPanel(this.host.hudHost, { title: d.name, subtitle: 'Dungeon entrance · Normal', className: 'fx-dungeon' });
    el('p', 'fx-say', panel.body, 'Cold air breathes out of the barrow. Something old is awake down there.');
    const meta = el('div', 'fx-dungeon-meta', panel.body);
    el('span', '', meta).innerHTML = `Level <b>${lo}–${hi}</b>`;
    el('span', '', meta).innerHTML = `Group Finder: <b>ready</b>`;
    el('span', '', meta).innerHTML = `First clear: <b>gear + XP</b>`;
    const partyRow = el('div', 'fx-party', panel.body);
    for (const id of dungeonParty(this.store.profile, this.partySeed(d.id))) {
      const spec = specInfo(id.includes('@') ? id.slice(id.indexOf('@') + 1) : null);
      const cls = this.host.content.class(baseClassIdOf(id))?.name ?? id;
      el('span', id === c.classId ? 'you' : '', partyRow, id === c.classId ? c.name : `${spec ? `${spec.name} ${cls}` : cls} (Group Finder)`);
    }
    if (level < lo) el('p', 'fx-quest-text', panel.body, `Recommended level ${lo}. You are level ${level}: it will be hard.`);
    const enter = el('button', 'fx-btn', panel.foot, 'Enter');
    enter.type = 'button';
    enter.addEventListener('click', () => {
      panel.close();
      void this.enterDungeon(d.id, { mapId: l.mapId, portalId: portal.id });
    });
    const no = el('button', 'fx-btn ghost', panel.foot, 'Not now');
    no.type = 'button';
    no.addEventListener('click', () => panel.close());
  }

  async enterDungeon(dungeonId: string, back: { mapId: string; portalId: string }): Promise<void> {
    if (this.auto.active) this.toggleAuto(false);
    this.savePosition();
    const r = await this.store.act('dungeonEnter', { dungeonId });
    if (!r.ok) return void kmToast(r.reason, 'error');
    // "Investigate the Glowing Ruin" completes on entering: shown once the dungeon is up.
    const g = r as unknown as FieldGain;
    if (g.completed?.length || g.levels > 0) this.after.push(() => this.applyGain(g));
    this.store.flush();
    await this.load(dungeonId, null, { back });
  }

  /** Back to the entrance (after a clear, a wipe-out or Leave). */
  private async leaveDungeon(cleared: boolean, reward?: () => void): Promise<void> {
    const l = this.live;
    const back = l?.back ?? this.entranceOf(l?.mapId ?? 'hollow_barrow');
    if (!back) return this.enter();
    if (reward) this.after.push(reward);
    if (!cleared && l) this.flushKills(l);
    // Step out in front of the entrance the party went in by.
    this.travel(back.mapId, back.portalId);
  }

  private entranceOf(dungeonId: string): { mapId: string; portalId: string } | null {
    for (const d of this.host.content.bundle.dungeons) {
      const p = d.field?.portals.find((x) => x.kind === 'dungeon' && x.to === dungeonId);
      if (p) return { mapId: d.id, portalId: p.id };
    }
    return null;
  }

  private sessionEnded(end: SessionEnd): void {
    const l = this.live;
    if (!l || l.kind !== 'dungeon') return;
    const dungeonId = l.mapId;
    if (!end.result.completed) return void this.leaveDungeon(false);
    this.flushKills(l);
    void this.store.act('dungeonClear', { dungeonId }).then((r) => {
      let show: (() => void) | undefined;
      if (r.ok) {
        const g = r as unknown as FieldGain & { first: boolean; item: { name?: string } | null; reward: { xp: number; gold: number } };
        const before = fieldView.level;
        show = () => {
          const lines = [`+${g.reward.xp} XP`, g.reward.gold ? `+${g.reward.gold} gold` : '', g.item ? `${itemLabel(g.item)}` : ''].filter(Boolean);
          celebrations.current?.small({ title: 'DUNGEON CLEARED', subtitle: `${l.map.name}${g.first ? ' · first clear' : ''}`, lines });
          playSound('bounty.complete');
          const live = this.live;
          if (live) live.session.renderer.celebrate?.(live.heroId, 'small');
          if (g.levels > 0) {
            const sum = levelUpSummary(this.live?.registry ?? l.registry, this.store.profile.character?.classId ?? '', before, g.level);
            for (const a of sum.abilities) fieldView.newUnlocks.add(a);
            const s = this.host.settings();
            celebrations.current?.levelUp({ from: before, to: g.level, gains: sum.gains, unitId: this.live?.heroId ?? null, flash: s.screenShake && s.fxIntensity > 0.25 });
            playSound('level.up');
          }
          this.refresh();
        };
      } else kmToast(`The clear could not be recorded: ${r.reason}`, 'error', 5000);
      void this.leaveDungeon(true, show);
    });
  }

  // -------------------------------------------------------------------------
  // Fade
  // -------------------------------------------------------------------------

  private async fadeOut(): Promise<void> {
    if (!this.live) return;
    const f = (this.fade ??= el('div', 'fx-fade', this.host.hudHost));
    f.classList.add('on');
    await new Promise((r) => setTimeout(r, FADE_MS));
  }

  private fadeIn(): void {
    const f = this.fade;
    if (!f) return;
    if (f.parentElement !== this.host.hudHost) this.host.hudHost.appendChild(f);
    requestAnimationFrame(() => requestAnimationFrame(() => f.classList.remove('on')));
  }

  // -------------------------------------------------------------------------
  // Debug (window.__keystone)
  // -------------------------------------------------------------------------

  /** Grant XP through the same path as kills: profile → level-up celebration + setLevel. */
  debugGiveXp(xp: number): void {
    const p = this.store.profile;
    const c = p.character;
    if (!c) return;
    const next: Profile = structuredClone(p);
    const ch = next.characters[c.classId];
    if (!ch) return;
    const from = ch.hero.level;
    const levels = addXp(ch, xp);
    this.store.set(next);
    this.applyGain({ xp, gold: 0, levels, level: from + levels, completed: [], bounties: [], chainAdvanced: false });
  }

  /** XP to reach `n` more levels (from the current XP). */
  debugLevelUp(n = 1): void {
    const p = this.store.profile;
    const hero = p.character ? p.characters[p.character.classId]?.hero : undefined;
    if (!hero) return;
    let xp = xpToNext(hero.level) - hero.xp;
    for (let i = 1; i < n; i++) xp += xpToNext(hero.level + i);
    this.debugGiveXp(xp);
  }

  debugAuto(on: boolean): void {
    this.toggleAuto(on);
  }

  debugTeleport(mapId: string): void {
    this.travel(mapId, null);
  }

  /** +quests: stand in front of a quest NPC (screenshots); opens the side quests first if needed. */
  async debugQuestNpc(npcId: string): Promise<void> {
    const l = this.live;
    const n = l?.map.field?.npcs?.find((x) => x.id === npcId);
    if (!l || !n) return;
    const c = this.store.profile.character;
    if (c && currentChainStep(c.bounties, c.zoneId)?.id === 'mv_main_1_elder') await this.store.act('bountyTalk', { npcId: 'elder_rowan' });
    const pos = { x: round2(n.pos.x + Math.cos(n.facing) * 3), y: round2(n.pos.y + Math.sin(n.facing) * 3) };
    const facing = round2(n.facing + Math.PI);
    await this.store.act('fieldMove', { mapId: l.mapId, pos, facing });
    await this.load(l.mapId, { pos, facing });
  }

  /** +quests: the nearest quest NPC barks (screenshots). */
  debugBark(): void {
    const l = this.live;
    const u = l?.session.sim.getWorld().units[l.heroId];
    if (u) this.quests.debugBark(u.pos);
  }

  /** Accept every bounty the board offers (screenshots). */
  debugBounties(): void {
    const c = this.store.profile.character;
    for (const b of zoneQuests(c?.zoneId ?? '')?.bounties ?? []) if (bountyDef(b.id)) void this.store.act('bountyAccept', { bountyId: b.id });
    this.refresh();
  }
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

const round2 = (n: number): number => Math.round(n * 100) / 100;

function safeCompanions(party: readonly string[], slot: number, seed: number, names: readonly string[]) {
  try {
    return defaultCompanions(party.filter((_, i) => i !== slot), seed, [...names]);
  } catch {
    return [];
  }
}

/** "Rare Helm (ilvl 208)" for a toast. */
function itemName(it: Item): string {
  const r = it.rarity[0]!.toUpperCase() + it.rarity.slice(1);
  const slot = it.slot[0]!.toUpperCase() + it.slot.slice(1);
  return `${r} ${slot} (ilvl ${it.ilvl})`;
}

function itemLabel(item: { name?: string }): string {
  return item.name ? `New gear: ${item.name}` : 'New gear';
}

/** +zone: the area name in big letters at the top of the screen (WoW-style zone text), fading out. */
function zoneText(host: HTMLElement, name: string, sub: string): void {
  host.querySelector('.fx-zonetext')?.remove();
  const box = el('div', 'fx-zonetext', host);
  box.style.cssText = 'position:absolute;left:50%;top:15%;transform:translateX(-50%);text-align:center;pointer-events:none;z-index:40;opacity:0;transition:opacity 700ms ease;font-family:inherit;';
  const t = el('div', '', box, name);
  t.style.cssText = 'font-size:34px;font-weight:700;letter-spacing:0.04em;color:#ffe9b0;text-shadow:0 2px 6px rgba(0,0,0,0.8),0 0 18px rgba(0,0,0,0.5);';
  const s = el('div', '', box, sub);
  s.style.cssText = 'font-size:15px;margin-top:2px;color:#e8e2d0;text-shadow:0 1px 4px rgba(0,0,0,0.85);';
  requestAnimationFrame(() => (box.style.opacity = '1'));
  setTimeout(() => (box.style.opacity = '0'), 2600);
  setTimeout(() => box.remove(), 3400);
}

function btn(parent: HTMLElement, label: string, onClick: () => unknown): HTMLButtonElement {
  const b = el('button', 'fx-btn', parent, label);
  b.type = 'button';
  b.addEventListener('click', () => {
    b.disabled = true;
    void Promise.resolve(onClick()).finally(() => (b.disabled = false));
  });
  return b;
}

function questBox(parent: HTMLElement, title: string, text: string | null, reward: { xp: number; gold: number }, ready: boolean, tag = ''): HTMLElement {
  const box = el('div', `fx-quest${ready ? ' ready' : ''}`, parent);
  const t = el('div', 'fx-quest-title', box, title);
  if (tag) el('small', '', t, tag);
  if (text) el('div', 'fx-quest-text', box, text);
  const r = el('div', 'fx-quest-reward', box);
  r.innerHTML = `Reward: <b>${reward.xp} XP</b>${reward.gold ? ` · <b>${reward.gold} gold</b>` : ''}`;
  return box;
}

/** What the quest givers say on the main quest's talk steps (by step id). */
const QUEST_LINES: Readonly<Record<string, (name: string) => string>> = {
  mv_main_0_pell: (n) => `Oh! You’re awake, dear. ${n}, was it? Snored like a badger on my bench, you did. Now, the slimes have been at my garden again, the cheeky blobs: pop a few down in the Hollows for me, would you? And then go and see Elder Rowan in Mossvale, up the road past the old shrine. He’ll know what to do with a strapping young thing like you. Mind the Tower doesn’t stare back. It does that.`,
  mv_main_1_elder: (n) => `Ah, ${n}! Granny Pell sent you? Then you’ve already met our slimes. Welcome to Mossvale. The boars at Brookside Farm grow bolder every day: go back west past the Hollows and up the farm track, and thin them out. The road will show you the way.`,
  mv_main_7_hearth: (n) => `Sit, ${n}, warm your hands. You have seen it now: Auto fights just as well as you do. Here is the secret of Mossvale's heroes: you never truly stop. And now that you can hold your own: folk around Mossvale post bounties on the board by the well. Take a look.`,
  mv_main_11_tower: (n) => `So you are ${n}. Word travels fast: slimes, boars, the barrow, the Briar gang. Look up. That spire on the horizon is the Tower, and no one climbs it unchanged. Take my Advancement Trial: choose the path your skills will follow.`,
};
/** Quest givers' small talk when they have nothing for you. */
const IDLE_LINES: Readonly<Record<string, string>> = {
  hearthkeeper: 'The hearth never goes out, and neither does a good hero. Come back if you want to hear about Auto again.',
  tower_warden: 'Not yet, friend. Reach level 10 in Mossvale first; then my Advancement Trial will show you the three paths your class can take.',
};
/** +zone: the lore stones (FieldNpcDef role 'lore'). */
const LORE_LINES: Readonly<Record<string, string>> = {
  lore_lake: 'Here the star-glass fell and the water closed over it. Glimmermere has shone at night ever since; the snappers dive for the shards.',
  lore_forest: 'The Bramble kings kept this road. When the last of them was buried under the Pale Rise, the thorns took the forest back.',
  lore_rise: 'From this hill the old wardens watched the Tower rise. They swore to guard the way to it until someone worthy came. Their stone still keeps the oath.',
  lore_heights: 'Light a candle for the deep. The Tallow folk dig for the star-glass under Greyhorn, and they do not like company.',
  lore_road: 'East to the lake, a day’s easy walking. Mind the hounds past the outpost, and drink before you climb.',
  lore_field: 'Here stood the first hall of Mossvale, before the barrow was sealed. Its stones went into the village well.',
  // +detail: the quiet country's points of interest.
  lore_eldertree: 'Hollowcrown. The first settlers slept under this oak the night they came over the hills, and the slimes kept their distance until morning. Nobody has cut so much as a twig from it since.',
  lore_beacon: 'The Old Beacon. A fire on this tower once answered the lights of the Pale Rise. When the wardens left, the last keeper stayed on until the oil ran out.',
  lore_ring: 'The Ring of Hours. Stand at its heart at noon and no stone throws its shadow on you. The children of Mossvale dare each other to stay here past dusk.',
  lore_statue: 'The Stone King, the farmers call him. Nobody remembers whose face it was; the old folk say he fell the night the Hollow Barrow was sealed, and has been reaching for it since.',
  lore_shrine: 'Leave a flower for the road. The shrine keeps a traveller’s boots dry and purse full, or so the carters swear.',
  lore_camp: 'Gone to the Barrow. Back by dark. Do not touch the pack.',
  // +glide: the beginner sky puzzle (Hollowcrown Boughs).
  lore_skyway: 'A note pinned to the post, in Granny Pell’s round hand: “Hop up the stones, then into the swirl. Hold Space in the air to glide, dear — the wind does the rest. There’s a little something at the top.”',
};
/** +zone: villagers outside town. */
const VILLAGER_LINES: Readonly<Record<string, string>> = {
  fisher_dunn: 'Something big lives off Wisp Isle. Snapped my best line clean through. Wade the shallows west of the isle if you dare.',
  villager_tobin: 'Boars in the barley again. And old Mossback, bigger than a cart, when the moon is up.',
  outpost_keeper: 'Welcome to the Lakeside Outpost. The board has work on the shore and up on Greyhorn. Bring the proof back here, not to the village.',
};
const ELDER_NUDGE = (n: string): string => `Go on, ${n}. Mossvale is counting on you: follow the gold marker.`;
